import { useAppDispatch, useAppSelector } from 'app/hooks';
import AlertModal from 'components/common/AlertModal';
import FeatureTitleBar from 'components/common/FeatureTitleBar';
import useGlobalModal from 'components/common/GlobalModal/useGlobalModal';
import ImageUploadModal from 'components/common/ImageUploadModal';
import MultiSelectDropdown from 'components/common/MultiSelectDropdown';
import useEscapeKey from 'components/common/UseEscapeKey/UseEscapeKey';
import { selectIsEditMode } from 'features/common/editModeSlice';
import { DisplayControlGroup } from 'features/DisplayControlGroup/models';
import { DisplayControlGroupType, MENU_DISPLAY_TIMING, MENU_DISPLAY_TIMING_SUB } from 'features/MenuList/constants';
import { Overlay } from 'features/OverlayFunction/models/overlay';
import React, { ChangeEvent, forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Button, Col, Form, Image, Modal, ModalProps, Row, Table } from 'react-bootstrap';
import 'react-datepicker/dist/react-datepicker.css';
import ReactDOMServer from 'react-dom/server';
import { useTranslation } from 'react-i18next';
import { NoImageIcon } from 'theme/images';
import { getCurrency, svgToDataURL } from 'utils/common';
import { IMAGE_SIZES } from 'utils/constants';
import { Menu, MenuDetail, MenuProperty } from '../../MenuList/models/menu';
import { PROPERTY_LIMIT, PROPERTY_PROVIDE } from '../constants';
import menuDetailService from '../menuDetailService';
import { menuDetailActions, selectMenu } from '../menuDetailSlice';
import { MenuDetailView } from '../models/menuDetail';
import { MenuView } from '../models/viewMenu';
import './MenuDetail.scss';
import services from './service';

const MenuDetailModal = forwardRef<
  ModalProps,
  { onMenuModalClose?: () => void; menuCallBack?: (obj: {} | undefined) => void }
>((props, ref) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // selectMenu
  const menuSelector = useAppSelector(selectMenu);
  //ref
  let menuImgFileRef = useRef<HTMLImageElement>(null);
  let menuImgName = useRef('');
  const { showGlobalModal } = useGlobalModal();

  // selectors
  const isEditMode = useAppSelector(selectIsEditMode);

  //state
  const [showModal, setShowModal] = useState(false);
  const [showImgUploadModal, setShowImgUploadModal] = useState(false);
  const [menuView, setMenuView] = useState<MenuView>();
  const [selectedMenu, setSelectedMenu] = useState<Menu>();
  const [menuPropertiesActive, setMenuPropertiesActive] = useState<Array<MenuProperty>>();
  const [externalMenuSelected, setExternalMenuSelected] = useState<Menu>();
  const [menuDetails, setMenuDetails] = useState<MenuDetailView[]>();
  const [propertiesProvide, setPropertiesProvider] = useState<Array<MenuProperty>>([]);
  const [propertiesLimit, setPropertiesLimit] = useState<Array<MenuProperty>>([]);

  //external menu
  const [selectedMenuId, setSelectedMenuId] = useState<number>();

  useEffect(() => {
    if (menuSelector && Object.keys(menuSelector).length > 0) {
      setExternalMenuSelected(menuSelector);
    } else {
      setExternalMenuSelected(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuSelector]); // call from external page

  useEffect(() => {
    if (selectedMenuId && externalMenuSelected && Object.keys(externalMenuSelected).length > 0) {
      let menu_view = new MenuView(externalMenuSelected);
      setMenuView(menu_view);
      setSelectedMenu(menu_view.menu);
      menuImgName.current = menu_view.menu.img || '';
      setMenuPropertiesActive(menu_view.properties_active());
      setShowModal(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [externalMenuSelected, selectedMenuId]); // call from external page

  //useImperativeHandle
  useImperativeHandle(ref, () => ({
    // call from internal parent page
    openModal(menu_detail: MenuDetail, expired_date: string) {
      if (menu_detail) {
        if (menu_detail.menu.id !== selectedMenu?.id) {
          let menu_view = new MenuView(
            menu_detail.menu,
            menu_detail.start_date.toString(),
            menu_detail.end_date.toString()
          );
          getMenuDetails(menu_detail.menu.id, expired_date);
          setMenuView(menu_view);
          setSelectedMenu(menu_view.menu);
          menuImgName.current = menu_view.menu.img || '';
          setMenuPropertiesActive(menu_view.properties_active());
        }
        setShowModal(true);
      }
    },
    // call from external parent page
    openModalExternal(menu_id: number, data_date: string = '') {
      if (menu_id) {
        dispatch(menuDetailActions.fetchMenu({ menu_id: menu_id, expired_date: data_date })); //data_date format string '2023830'
        getMenuDetails(menu_id, data_date);
        setSelectedMenuId(menu_id);
      }
    },
    onReset() {
      setSelectedMenuId(undefined);
      setMenuView(undefined);
      setSelectedMenu(undefined);
      setMenuPropertiesActive(undefined);
      setExternalMenuSelected(undefined);
      dispatch(menuDetailActions.removeStateMenu({} as Menu));
    },
  }));

  const getMenuDetails = async (menu_id: number, str_date: string = '') => {
    try {
      const response = await menuDetailService.getMenuDetails({ menu_id: menu_id, expired_date: str_date });
      setMenuDetails(response.data);
    } catch (error) {
      console.error('Error fetching CSV:', error);
    }
  };

  const handleChangeMenuAttribute = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const property_name = event.currentTarget.name;
    const value = event.currentTarget.value;
    if (selectedMenu) {
      const obj = {
        [property_name]: value,
      };
      setSelectedMenu({ ...selectedMenu, img: menuImgName.current, ...obj });
    }
  };

  useEffect(() => {
    if (menuPropertiesActive) {
      // Filter menuPropertiesActive by text and set to propertiesProvide
      const filteredProvide = menuPropertiesActive.filter(
        (prop) => PROPERTY_PROVIDE.includes(prop.text) && prop.text.trim() !== ''
      );
      setPropertiesProvider(filteredProvide);
      // Filter menuPropertiesActive by limit and set to propertiesLimit
      const filteredLimit = menuPropertiesActive.filter(
        (prop) => PROPERTY_LIMIT.includes(prop.text) && prop.text.trim() !== ''
      );
      setPropertiesLimit(filteredLimit);
    }
  }, [menuPropertiesActive]);

  const handleChangeServiceTiming = (event: ChangeEvent<HTMLSelectElement>) => {
    if (event.currentTarget.value !== '' && selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        timing_id: parseInt(event.currentTarget.value),
      });
    }
  };

  const [menuServicesTime, setMenuServicesTime] = useState<{ id: number; name: string }[]>([]);
  useEffect(() => {
    services.getMenuServiceTime().then((response) => {
      setMenuServicesTime(response.data);
    });
  }, []);

  const handleChangeMenuProperty = (event: ChangeEvent<HTMLInputElement>) => {
    if (selectedMenu && menuPropertiesActive && menuPropertiesActive.length > 0) {
      const newPropsActive = menuPropertiesActive?.map((obj) => {
        if (obj.property === parseInt(event.currentTarget.value)) {
          return { ...obj, is_active: event.currentTarget.checked };
        }
        return obj;
      });
      setMenuPropertiesActive(newPropsActive);
      setSelectedMenu({ ...selectedMenu, menu_properties: newPropsActive, img: menuImgName.current });
    }
  };
  const handleChangeMenuDisplayTiming = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      const displayTiming = parseInt(event.currentTarget.value);
      setSelectedMenu({
        ...selectedMenu,
        display_timing: displayTiming,
        is_disable_control_group: displayTiming === 6 ? false : true,
        img: menuImgName.current,
      });
    }
  };

  const handleChangeMenuSubDisplayTiming = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        sub_display_timing: parseInt(event.currentTarget.value),
      });
    }
  };

  const handleChangeOverlayMenu = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        overlay_group: parseInt(event.currentTarget.value),
      });
      setOverlayGroupError(false);
    }
  };

  const handleChangeSubOverlayMenu = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        sub_overlay_group: parseInt(event.currentTarget.value),
      });
      setSubOverlayGroupError(false);
    }
  };

  const handleResFromUploadImgModal = (filename: string, image_path: string) => {
    if (menuImgFileRef && menuImgFileRef.current) {
      if (selectedMenu) {
        menuImgName.current = filename;
        setSelectedMenu({ ...selectedMenu, img: filename });
      }
    }
  };

  const [overlayGroupError, setOverlayGroupError] = useState<boolean>(false);
  const [subOverlayGroupError, setSubOverlayGroupError] = useState<boolean>(false);
  const handleSaveMenuUpdate = async () => {
    if (selectedMenu) {
      const menuCloneObject = { ...selectedMenu };
      menuCloneObject.img = menuCloneObject.img || '';
      await menuDetailService.patchMenu(menuCloneObject).then((res) => {
        showGlobalModal &&
          showGlobalModal(() => (
            <AlertModal
              title={t('menuDetailFeature.Form.title_success')}
              message={t('menuDetailFeature.Form.save_success')}
              onClose={() => handleCloseModal(true)}
            ></AlertModal>
          ));
      });
    }
  };

  const handleCloseModal = (is_update = false) => {
    if (is_update === true && props.menuCallBack !== undefined) {
      const menuCloneObject = { ...selectedMenu, img: menuImgName.current };
      props.menuCallBack(menuCloneObject);
    }
    if (props.onMenuModalClose !== undefined) {
      props.onMenuModalClose();
    }
    setShowModal(false);
    setOverlayGroupError(false);
    setSubOverlayGroupError(false);
  };

  useEscapeKey(handleCloseModal);

  const getSvgDataUrl = useCallback(() => {
    const SvgComponent = React.memo(() => (
      <NoImageIcon
        width={150}
        height={100}
        display_txt={`${IMAGE_SIZES.menu_img.minWidth}x${IMAGE_SIZES.menu_img.minHeight}~${IMAGE_SIZES.menu_img.maxWidth}x${IMAGE_SIZES.menu_img.maxHeight}`}
      />
    ));
    const svgString = ReactDOMServer.renderToString(<SvgComponent />);
    return svgToDataURL(svgString);
  }, []);

  const handleMultiSelectChangeControlGroupMenu = (selectedValues: number[]) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        display_control_groups: selectedValues,
      });
    }
  };

  const handleMultiSelectChangeSubControlGroupMenu = (selectedValues: number[]) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        sub_display_control_groups: selectedValues,
      });
    }
  };

  const handleChangeOrderQuantityLimitType = (event: ChangeEvent<HTMLSelectElement>) => {
    if (selectedMenu) {
      setSelectedMenu({
        ...selectedMenu,
        order_quantity_limit_type: parseInt(event.currentTarget.value),
      });
    }
  };

  const [overlayGroups, setOverlayGroups] = useState<Overlay[]>([]);
  useEffect(() => {
    services.getOverlayGroups().then((res) => {
      const data = res.data.filter((item: Overlay) => !item.delete_flg);
      data && setOverlayGroups(data);
    });
  }, [selectedMenu]);

  const [listDisplayControlGroup, setDisplayControlGroup] = useState<DisplayControlGroupType[]>([]);
  useEffect(() => {
    services.getDisplayControlGroups().then((res) => {
      const data: DisplayControlGroupType[] = res.data
        .filter((item: DisplayControlGroup) => !item.delete_flg)
        .map((item: DisplayControlGroup) => ({
          value: item.id,
          key: item.name,
          display_name: item.display_name,
        }));
      setDisplayControlGroup(data);
    });
  }, []); // Add an empty dependency array here

  const [listMenuOrderQuantityLimit, setListMenuOrderQuantityLimit] = useState<{ key: string; value: string }[]>([]);
  useEffect(() => {
    services.getListOrderQuantityLimit().then((res) => {
      setListMenuOrderQuantityLimit(res.data);
    });
  }, []); // Add an empty dependency array here

  return (
    <>
      {showImgUploadModal && (
        <ImageUploadModal
          imagepath={menuImgName.current ? process.env.REACT_APP_IMAGE_URL + menuImgName.current : ''}
          minWidth={IMAGE_SIZES.menu_img.minWidth}
          minHeight={IMAGE_SIZES.menu_img.minHeight}
          maxWidth={IMAGE_SIZES.menu_img.maxWidth}
          maxHeight={IMAGE_SIZES.menu_img.maxHeight}
          maxFileSize={IMAGE_SIZES.menu_img.maxFileSize}
          onReflect={handleResFromUploadImgModal}
          onClose={() => {
            setShowImgUploadModal(false);
          }}
        ></ImageUploadModal>
      )}
      <div className="static-modal">
        <Modal animation={true} size="xl" centered={true} show={showModal} backdrop={'static'} keyboard={false}>
          <Modal.Body className={isEditMode ? 'reedit-mode-wrapper' : ''}>
            <div className="row">
              <div className=" col-sm-5">
                <FeatureTitleBar title={t('menuDetailFeature.Text.menu_detail_normal')}></FeatureTitleBar>
              </div>
              <div className="col text-end">
                <button type="button" className="btn-close" onClick={() => handleCloseModal()}></button>
              </div>
            </div>
            <Row className="mb-3">
              <Col className="col-sm-10 col-xs-12">
                <div className="d-flex align-items-end" style={{ justifyContent: 'right' }}>
                  <button onClick={handleSaveMenuUpdate} type="button" className="btn btn-sm btn-success">
                    {t('menuDetailFeature.Component.save_btn')}
                  </button>
                </div>
              </Col>
            </Row>
            <Row className="mb-3">
              <Col className="col-sm-6 col-xs-12">
                <div className="mb-3 d-flex justify-content-between">
                  <div>
                    <h5>{t('menuDetailFeature.Text.menu_settings')}</h5>
                    <div dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.notice_text') }}></div>
                  </div>
                </div>
                <div>
                  <Table className="table table-bordered text-center align-middle mb-3">
                    <tbody>
                      <tr className="mb-3">
                        <td className="table-primary col-md-4" colSpan={2}>
                          {t('menuDetailFeature.Text.menu_name')}
                        </td>
                        <td className="col-md-6">{selectedMenu?.food_system_menu?.name || ''}</td>
                      </tr>
                      <tr className="mb-3">
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.display_timing')}
                        </td>
                        <td>
                          <div className="row justify-content-center align-items-center">
                            <div className="col-xs-4 col-sm-4">
                              <span>{t('menuDetailFeature.Text.control_condition')}</span>
                            </div>
                            <div className="col-xs-8 col-sm-8">
                              <Form.Select
                                onChange={handleChangeMenuDisplayTiming}
                                defaultValue={selectedMenu?.default_value_display_timing}
                              >
                                {MENU_DISPLAY_TIMING.map((condition, index) => {
                                  return (
                                    <option key={index} value={condition.value}>
                                      {condition.key}
                                    </option>
                                  );
                                })}
                              </Form.Select>
                            </div>
                          </div>

                          <div className="row align-items-center mt-3">
                            <div className="col-xs-4 col-sm-4">
                              <div
                                dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.display_control_group') }}
                              ></div>
                            </div>
                            <div className="col-xs-8 col-sm-8 mb-3">
                              <MultiSelectDropdown
                                defaultText={'選択無し'}
                                options={listDisplayControlGroup}
                                selectedValues={selectedMenu?.display_control_groups || []}
                                onChange={handleMultiSelectChangeControlGroupMenu}
                                isDisabled={selectedMenu?.is_disable_control_group}
                              />
                            </div>
                            <div dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.note3') }}></div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.overlay_group')}
                        </td>
                        <td>
                          <div className="text-center d-flex justify-content-around mt-3 mb-3">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Select
                                onChange={handleChangeOverlayMenu}
                                defaultValue={selectedMenu?.overlay_group || 0}
                              >
                                <option value="0">{t('menuDetailFeature.Text.default_overlay_group')}</option>
                                {overlayGroups.map((overlay, index) => {
                                  return (
                                    <option key={index} value={overlay.id}>
                                      {overlay.name}
                                    </option>
                                  );
                                })}
                              </Form.Select>
                              <div style={{ textAlign: 'left' }}>
                                {overlayGroupError && (
                                  <span className="form-control-feedback">{t('menuDetailFeature.Form.required')}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.menu_property')}
                        </td>
                        <td>
                          <div className="row">
                            <Col className="col-sm-12 col-xs-12 mb-3">
                              <fieldset className="border rounded-3 px-3">
                                <legend className="float-none small w-auto px-3 mb-0 text-start">
                                  {t('menuDetailFeature.Text.provide_content')}
                                </legend>
                                <Row className="mb-1">
                                  {(() => {
                                    const half = Math.ceil(propertiesProvide.length / 2);
                                    const leftColumn = propertiesProvide.slice(0, half);
                                    const rightColumn = propertiesProvide.slice(half);
                                    return (
                                      <>
                                        <Col sm={6}>
                                          {leftColumn.map((checkbox, idx) => (
                                            <div className="checkbox-container" key={idx}>
                                              <Form.Check
                                                inline
                                                onChange={handleChangeMenuProperty}
                                                type="checkbox"
                                                value={checkbox.property}
                                                checked={checkbox.is_active}
                                                label={checkbox.text}
                                                id={`provide_property_left_${checkbox.property}`}
                                              />
                                            </div>
                                          ))}
                                        </Col>
                                        <Col sm={6}>
                                          {rightColumn.map((checkbox, idx) => (
                                            <div className="checkbox-container" key={idx}>
                                              <Form.Check
                                                inline
                                                onChange={handleChangeMenuProperty}
                                                type="checkbox"
                                                value={checkbox.property}
                                                checked={checkbox.is_active}
                                                label={checkbox.text}
                                                id={`provide_property_right_${checkbox.property}`}
                                              />
                                            </div>
                                          ))}
                                        </Col>
                                      </>
                                    );
                                  })()}
                                </Row>
                              </fieldset>
                            </Col>
                            <Col className="col-sm-12 col-xs-12 mb-3">
                              <fieldset className="border rounded-3 px-3">
                                <legend className="float-none small w-auto px-3 mb-0 text-start">
                                  {t('menuDetailFeature.Text.limit_and_option')}
                                </legend>
                                <Row className="mb-1">
                                  {(() => {
                                    const half = Math.ceil(propertiesLimit.length / 2);
                                    const leftColumn = propertiesLimit.slice(0, half);
                                    const rightColumn = propertiesLimit.slice(half);
                                    return (
                                      <>
                                        <Col sm={6}>
                                          {leftColumn.map((checkbox, idx) => (
                                            <div className="checkbox-container" key={idx}>
                                              <Form.Check
                                                inline
                                                onChange={handleChangeMenuProperty}
                                                type="checkbox"
                                                value={checkbox.property}
                                                checked={checkbox.is_active}
                                                label={checkbox.text}
                                                id={`limit_property_left_${checkbox.property}`}
                                              />
                                            </div>
                                          ))}
                                        </Col>
                                        <Col sm={6}>
                                          {rightColumn.map((checkbox, idx) => (
                                            <div className="checkbox-container" key={idx}>
                                              <Form.Check
                                                inline
                                                onChange={handleChangeMenuProperty}
                                                type="checkbox"
                                                value={checkbox.property}
                                                checked={checkbox.is_active}
                                                label={checkbox.text}
                                                id={`limit_property_right_${checkbox.property}`}
                                              />
                                            </div>
                                          ))}
                                        </Col>
                                      </>
                                    );
                                  })()}
                                </Row>
                              </fieldset>
                            </Col>
                            <Col className="col-sm-12 col-xs-12 mb-3 text-center">
                              <fieldset className="border rounded-3 px-3">
                                <legend className="float-none small w-auto px-3 mb-0 text-start">
                                  {t('menuDetailFeature.Text.limit_and_option_enabled')}
                                </legend>
                                <Row className="mb-3 mt-3">
                                  <Col sm={9} className="ms-3 text-center">
                                    <Form.Select
                                      disabled={!selectedMenu?.timing_flg}
                                      onChange={handleChangeServiceTiming}
                                      value={selectedMenu?.timing_id ?? ''}
                                      id="menu_timing"
                                    >
                                      {(!selectedMenu?.timing_flg || selectedMenu?.timing_flg === null) && (
                                        <option value="" disabled>
                                          {t('menuDetailFeature.Text.invalid')}
                                        </option>
                                      )}
                                      {menuServicesTime.map((service) => (
                                        <option key={service.id} value={service.id}>
                                          {selectedMenu?.timing_flg ? service.name : ''}
                                        </option>
                                      ))}
                                    </Form.Select>
                                  </Col>
                                </Row>
                              </fieldset>
                            </Col>
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.order_limit_type')}
                        </td>
                        <td>
                          <div className="row mb-3 d-flex " style={{ paddingLeft: '2rem' }}>
                            <div className="col-xs-12">
                              <div
                                style={{ textAlign: 'left' }}
                                dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.note4') }}
                              ></div>
                            </div>
                            <div className="text-center justify-content-around mt-3 mb-3">
                              <div className="col-xs-12 col-sm-9">
                                <Form.Select
                                  onChange={handleChangeOrderQuantityLimitType}
                                  defaultValue={selectedMenu?.order_quantity_limit_type || 0}
                                >
                                  <option value="0">{t('menuDetailFeature.Text.default_order_limit')}</option>
                                  {listMenuOrderQuantityLimit.map((order, index) => {
                                    return (
                                      <option key={index} value={order.key}>
                                        {order.value}
                                      </option>
                                    );
                                  })}
                                </Form.Select>
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.menu_image')}
                        </td>
                        <td>
                          <div className="text-center d-flex justify-content-around mt-3 mb-3">
                            <div>
                              {
                                <Image
                                  style={{
                                    width: 150,
                                    height: 100,
                                  }}
                                  ref={menuImgFileRef}
                                  src={
                                    menuImgName.current
                                      ? process.env.REACT_APP_IMAGE_URL + menuImgName.current
                                      : getSvgDataUrl()
                                  }
                                  alt="No Image"
                                />
                              }
                            </div>
                            <div className="align-self-end">
                              <Button
                                onClick={() => {
                                  setShowImgUploadModal(true);
                                }}
                                variant="primary"
                              >
                                {t('menuDetailFeature.Component.change_btn')}
                              </Button>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.display_name')}
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_ja"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.display_name_ja ? selectedMenu.display_name_ja : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_en"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.display_name_en ? selectedMenu.display_name_en : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_zh_hans"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={
                                  selectedMenu?.display_name_zh_hans ? selectedMenu.display_name_zh_hans : ''
                                }
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_zh_hant"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={
                                  selectedMenu?.display_name_zh_hant ? selectedMenu.display_name_zh_hant : ''
                                }
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_ko"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.display_name_ko ? selectedMenu.display_name_ko : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="display_name_vi"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.display_name_vi ? selectedMenu.display_name_vi : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.explanation')}
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <textarea
                                className="form-control"
                                rows={2}
                                name="explanation_ja"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_ja ? selectedMenu.explanation_ja : ''}
                              ></textarea>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <textarea
                                className="form-control"
                                rows={2}
                                name="explanation_en"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_en ? selectedMenu.explanation_en : ''}
                              ></textarea>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <textarea
                                className="form-control"
                                rows={2}
                                name="explanation_zh_hans"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_zh_hans ? selectedMenu.explanation_zh_hans : ''}
                              ></textarea>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <textarea
                                className="form-control"
                                rows={2}
                                name="explanation_zh_hant"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_zh_hant ? selectedMenu.explanation_zh_hant : ''}
                              ></textarea>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <textarea
                                className="form-control"
                                rows={2}
                                name="explanation_ko"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_ko ? selectedMenu.explanation_ko : ''}
                              ></textarea>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <textarea
                                rows={2}
                                className="form-control"
                                name="explanation_vi"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.explanation_vi ? selectedMenu.explanation_vi : ''}
                              ></textarea>
                            </div>
                          </div>
                        </td>
                      </tr>

                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.note1')}
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note1_ja"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note1_ja ? selectedMenu.note1_ja : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note1_en"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note1_en ? selectedMenu.note1_en : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note1_zh_hans"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note1_zh_hans ? selectedMenu.note1_zh_hans : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note1_zh_hant"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note1_zh_hant ? selectedMenu.note1_zh_hant : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note1_ko"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note1_ko ? selectedMenu.note1_ko : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note1_vi"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note1_vi ? selectedMenu.note1_vi : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" rowSpan={6}>
                          {t('menuDetailFeature.Text.note2')}
                        </td>
                        <td className="table-primary">{t('menuDetailFeature.Text.japanese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note2_ja"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note2_ja ? selectedMenu.note2_ja : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.english')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note2_en"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note2_en ? selectedMenu.note2_en : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.simplified_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note2_zh_hans"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note2_zh_hans ? selectedMenu.note2_zh_hans : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.traditional_chinese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note2_zh_hant"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note2_zh_hant ? selectedMenu.note2_zh_hant : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.korean')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note2_ko"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note2_ko ? selectedMenu.note2_ko : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary">{t('menuDetailFeature.Text.vietnamese')}</td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Control
                                name="note2_vi"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note2_vi ? selectedMenu.note2_vi : ''}
                              ></Form.Control>
                            </div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.remarks')}
                        </td>
                        <td>
                          <div className="row justify-content-center">
                            <div className="col-xs-12 col-sm-8">
                              <textarea
                                name="note"
                                onChange={handleChangeMenuAttribute}
                                defaultValue={selectedMenu?.note ? selectedMenu.note : ''}
                                className="form-control"
                                rows={4}
                              ></textarea>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                </div>
              </Col>
              <Col className="col-sm-6 col-xs-12">
                <div className="mb-3">
                  <h5>{t('menuDetailFeature.Text.sub_menu_settings')}</h5>
                  <Table className="table table-bordered text-center align-middle mb-3">
                    <tbody>
                      <tr>
                        <td className="table-primary col-md-4" colSpan={2}>
                          {t('menuDetailFeature.Text.sub_menu_property')}
                        </td>
                        <td>{selectedMenu?.is_sub ? '〇' : 'X'}</td>
                      </tr>
                      <tr>
                        <td className="table-primary col-md-6" colSpan={2}>
                          {t('menuDetailFeature.Text.display_timing')}
                        </td>
                        <td>
                          <div className="row justify-content-center align-items-center">
                            <div className="col-xs-5 col-sm-5">
                              <span>{t('menuDetailFeature.Text.control_condition')}</span>
                            </div>
                            <div className="col-xs-7 col-sm-7">
                              <Form.Select
                                onChange={handleChangeMenuSubDisplayTiming}
                                defaultValue={selectedMenu?.sub_display_timing}
                              >
                                {MENU_DISPLAY_TIMING_SUB.map((condition, index) => {
                                  return (
                                    <option key={index} value={condition.value}>
                                      {condition.key}
                                    </option>
                                  );
                                })}
                              </Form.Select>
                            </div>
                          </div>

                          <div className="row align-items-center mt-3">
                            <div className="col-xs-5 col-sm-5">
                              <div
                                dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.display_control_group') }}
                              ></div>
                            </div>
                            <div className="col-xs-7 col-sm-7 mb-3">
                              <MultiSelectDropdown
                                defaultText={'選択無し'}
                                options={listDisplayControlGroup}
                                selectedValues={selectedMenu?.sub_display_control_groups || []}
                                onChange={handleMultiSelectChangeSubControlGroupMenu}
                                isDisabled={selectedMenu?.sub_display_timing !== 6}
                              />
                            </div>
                            <div dangerouslySetInnerHTML={{ __html: t('menuDetailFeature.Text.note3') }}></div>
                          </div>
                        </td>
                      </tr>
                      <tr>
                        <td className="table-primary" colSpan={2}>
                          {t('menuDetailFeature.Text.overlay_group')}
                        </td>
                        <td>
                          <div className="text-center d-flex justify-content-around mt-3 mb-3">
                            <div className="col-xs-12 col-sm-8">
                              <Form.Select
                                onChange={handleChangeSubOverlayMenu}
                                defaultValue={selectedMenu?.sub_overlay_group || 0}
                              >
                                <option value="0">{t('menuDetailFeature.Text.default_overlay_group')}</option>
                              </Form.Select>
                              <div style={{ textAlign: 'left' }}>
                                {subOverlayGroupError && (
                                  <span className="form-control-feedback">{t('menuDetailFeature.Form.required')}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </Table>
                  <div className="mb-3" style={{ marginBottom: '20px' }}></div>
                  <div className="mb-3">
                    <h5>{t('menuDetailFeature.Text.register_information')}</h5>
                    <Table className="table table-bordered text-center align-middle mb-3">
                      <tbody>
                        <tr>
                          <td rowSpan={2} className="col-md-4" style={{ backgroundColor: '#CFC9C9' }}>
                            {t('menuDetailFeature.Table.date_of_expired')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }}>{t('menuDetailFeature.Table.start')}</td>
                          <td>{menuView?.startDate}</td>
                        </tr>
                        <tr>
                          <td style={{ backgroundColor: '#CFC9C9' }}>{t('menuDetailFeature.Table.end')}</td>
                          <td>{menuView?.endDate}</td>
                        </tr>
                      </tbody>
                    </Table>
                  </div>
                  <div>
                    <Table className="table table-bordered text-center align-middle mb-3">
                      <thead>
                        <tr className="align-middle">
                          <td style={{ backgroundColor: '#CFC9C9' }} rowSpan={2}>
                            {t('menuDetailFeature.Table.pos_cd')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }} rowSpan={2}>
                            {t('menuDetailFeature.Table.pos_name')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }} rowSpan={2}>
                            {t('menuDetailFeature.Table.amount_of_money_excluding_tax')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }} colSpan={2}>
                            {t('menuDetailFeature.Table.normal')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }} colSpan={2}>
                            {t('menuDetailFeature.Table.sub')}
                          </td>
                        </tr>
                        <tr className="align-middle">
                          <td style={{ backgroundColor: '#CFC9C9' }}>{t('menuDetailFeature.Table.normal')}</td>
                          <td style={{ backgroundColor: '#CFC9C9' }}>
                            {t('menuDetailFeature.Table.normal_price_including_tax')}
                          </td>
                          <td style={{ backgroundColor: '#CFC9C9' }}>{t('menuDetailFeature.Table.sub')}</td>
                          <td style={{ backgroundColor: '#CFC9C9' }}>
                            {t('menuDetailFeature.Table.sub_price_including_tax')}
                          </td>
                        </tr>
                      </thead>
                      <tbody>
                        {menuDetails &&
                          menuDetails.map((menu_detail, index) => {
                            return (
                              <tr key={index}>
                                <td>{menu_detail.pos_code.cd}</td>
                                <td>{menu_detail.name}</td>
                                <td>
                                  {getCurrency(menu_detail.standard_price) ?? t('menuDetailFeature.Text.not_obtained')}
                                </td>
                                <td>{menu_detail.pos_code.normal_flg ? '〇' : 'ー'}</td>
                                <td>
                                  {menu_detail.pos_code.main_price
                                    ? getCurrency(menu_detail.pos_code.main_price)
                                    : '未取得'}
                                </td>
                                <td>{menu_detail.pos_code.sub_flg ? '〇' : 'ー'}</td>
                                <td>
                                  {menu_detail.pos_code.composition_price
                                    ? getCurrency(menu_detail.pos_code.composition_price)
                                    : '未取得'}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </Table>
                  </div>
                </div>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer className={isEditMode ? 'reedit-mode-wrapper' : ''}>
            <button className="btn btn-danger" onClick={() => handleCloseModal()}>
              {t('menuDetailFeature.Component.close')}
            </button>
          </Modal.Footer>
        </Modal>
      </div>
    </>
  );
});

export default MenuDetailModal;
